import { useState } from "react";
import { trpc } from "../../lib/trpc";
import { parseTrNumber } from "../../lib/trNumber";
import { Icon } from "../ui/Icon";
import { Drawer } from "../Drawer";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import type { ToastItem } from "../ui/Toast";

/**
 * Pozisyonun FAZLA MESAİ SAAT ÜCRETİ — tarih versiyonlu geçmiş + yeni oran.
 *
 * Yalnız süper-admin açar; uçlar da `assertSalaryAccess` ile korunur — buradaki gizleme
 * bir GÖRÜNÜM kolaylığıdır, güvenlik kapısı sunucudadır.
 *
 * Girilen tutar bir SAATLİK mesainin fiyatıdır; bordro katsayısı (1,5) buna UYGULANMAZ.
 * Katsayı yalnız oranı tanımlı olmayan personelde (maaştan türetme) çalışır.
 */

type CurrencyCode = "TRY" | "USD" | "EUR" | "GBP";

/** TR para gösterimi. Oran 4 ondalık taşıyabilir (Decimal(14,4)) → üst sınır 4. */
export const fmtRate = (n: number): string =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);

const fmtDay = (v: string | Date | null | undefined): string => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
};

export function OvertimeRateDrawer({
  open, position, onClose, pushToast,
}: {
  open: boolean;
  position: { id: string; title: string } | null;
  onClose: () => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();
  const positionId = position?.id ?? "";
  const listQ = trpc.hr.overtimeRate.list.useQuery({ positionId }, { enabled: open && !!positionId });
  const rows = listQ.data ?? [];

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<CurrencyCode>("TRY");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState("");
  const [delId, setDelId] = useState<string | null>(null);

  const invalidate = () => {
    qc.hr.overtimeRate.list.invalidate({ positionId });
    qc.hr.overtimeRate.currentAll.invalidate();
  };
  const addMut = trpc.hr.overtimeRate.create.useMutation({
    onSuccess: () => {
      invalidate();
      setAmount(""); setEffectiveFrom(""); setNote("");
      pushToast({ tone: "success", message: "Mesai saat ücreti kaydedildi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }),
  });
  const delMut = trpc.hr.overtimeRate.remove.useMutation({
    onSuccess: () => {
      invalidate();
      setDelId(null);
      pushToast({ tone: "success", message: "Oran silindi." });
    },
    onError: (e) => { setDelId(null); pushToast({ tone: "danger", title: "Silinemedi", message: e.message }); },
  });

  // TR ondalık: "150,50" → 150.5 (kanonik parseTrNumber — finansal yol).
  const amountNum = parseTrNumber(amount);
  const canAdd = amount.trim().length > 0 && !Number.isNaN(amountNum) && amountNum >= 0
    && !!effectiveFrom && !addMut.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={position ? "Fazla Mesai Ücreti — " + position.title : "Fazla Mesai Ücreti"}
      widthPx={640}
    >
      {!position ? null : (
        <>
          <p className="sub" style={{ margin: "0 0 12px" }}>
            Bir <b>saatlik</b> fazla mesainin ücreti. Bu pozisyondaki personelin mesai ücreti{" "}
            <b>mesai saati × bu tutar</b> olarak hesaplanır — bordro katsayısı (1,5) uygulanmaz.
            Personel kartında kişiye özel bir oran varsa o kişide kişinin oranı geçerlidir.
          </p>

          <section className="d-card">
            <div className="d-card-head">
              <div className="left"><span className="t">Oran Geçmişi</span><span className="s">en yeni önce</span></div>
            </div>
            <div className="d-card-body flush">
              {listQ.isError ? (
                <div className="mt-empty">{listQ.error.message}</div>
              ) : listQ.isLoading ? (
                <div className="mt-empty">Yükleniyor…</div>
              ) : rows.length === 0 ? (
                <div className="mt-empty">
                  Bu pozisyona henüz mesai saat ücreti tanımlanmamış — mesai ücreti maaştan türetilir
                  (brüt ÷ saatlik bölen × katsayı).
                </div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th className="num" style={{ width: 150 }}>Saat Ücreti</th>
                      <th style={{ width: 120 }}>Geçerli</th>
                      <th style={{ width: 120 }}>Bitiş</th>
                      <th>Not</th>
                      <th style={{ width: 48 }} aria-label="İşlemler" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="cell-num">{fmtRate(r.amount)} <span className="unit">{r.currency}</span></td>
                        <td className="mono">{fmtDay(r.effectiveFrom)}</td>
                        <td className="mono">
                          {r.effectiveTo ? fmtDay(r.effectiveTo) : <span style={{ color: "var(--neutral-400)" }}>Açık</span>}
                        </td>
                        <td>{r.note ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                        <td>
                          <button
                            type="button"
                            className="iact danger"
                            title="Sil"
                            aria-label="Bu oranı sil"
                            onClick={() => setDelId(r.id)}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="d-card" style={{ marginTop: 12 }}>
            <div className="d-card-head"><div className="left"><span className="t">Yeni Oran</span></div></div>
            <div className="d-card-body">
              <div className="form-row">
                <div className="form-field">
                  <label className="lbl" htmlFor="otr-amount">Saat Ücreti <span className="req">*</span></label>
                  <input
                    id="otr-amount"
                    className="form-input mono"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0,00"
                  />
                  <span className="help">Bir saatlik fazla mesainin ücreti — aylık maaş DEĞİL.</span>
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor="otr-cur">Para Birimi</label>
                  <select
                    id="otr-cur"
                    className="form-select"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
                  >
                    {(["TRY", "USD", "EUR", "GBP"] as CurrencyCode[]).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="lbl" htmlFor="otr-from">Geçerlilik Başlangıcı <span className="req">*</span></label>
                  <input
                    id="otr-from"
                    className="form-input"
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                  <span className="help">Önceki açık oran bu tarihte kapatılır — geçmiş bordrolar eski oranla kalır.</span>
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor="otr-note">Not</label>
                  <input
                    id="otr-note"
                    className="form-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn-md primary"
                disabled={!canAdd}
                onClick={() => addMut.mutate({
                  positionId,
                  amount: amountNum,
                  currency,
                  effectiveFrom,
                  note: note.trim() || null,
                })}
              >
                <Icon name="plus" size={14} />{addMut.isPending ? "Kaydediliyor…" : "Oranı Kaydet"}
              </button>
            </div>
          </section>

          <ConfirmDialog
            open={!!delId}
            title="Oranı sil"
            message="Bu mesai saat ücreti kaydı silinecek. Onaylı/ödenmiş bir bordronun dayanağıysa işlem reddedilir."
            confirmLabel="Sil"
            tone="danger"
            busy={delMut.isPending}
            onCancel={() => setDelId(null)}
            onConfirm={() => { if (delId) delMut.mutate({ id: delId }); }}
          />
        </>
      )}
    </Drawer>
  );
}
